import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Navigate, Link } from 'react-router-dom';

const Home: React.FC = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  
  const { currentUser, login, signup, loginWithGoogle } = useAuth();

  // If user is already logged in, redirect to cafes
  if (currentUser) {
    return <Navigate to="/cafes" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      
      if (isLogin) {
        await login(email, password);
      } else {
        await signup(email, password);
      }
    } catch (error: any) {
      setError(error.message);
    }
    
    setLoading(false);
  };

  const handleGoogleLogin = async () => {
    try {
      setError('');
      setLoading(true);
      await loginWithGoogle();
    } catch (error: any) {
      setError(error.message);
    }
    setLoading(false);
  };

  const features = [
    {
      icon: '📱',
      title: 'Mobile-First Design',
      description: 'Optimized for tablets and smartphones, perfect for busy cafe environments.'
    },
    {
      icon: '🍽️',
      title: 'Table Management',
      description: 'QR code ordering system allows customers to order directly from their table.'
    },
    {
      icon: '📋',
      title: 'Menu Management',
      description: 'Easy-to-use interface for updating menu items, prices, and availability.'
    },
    {
      icon: '👨‍🍳',
      title: 'Kitchen Dashboard',
      description: 'Real-time order tracking and management for kitchen staff.'
    },
    {
      icon: '📊',
      title: 'Analytics',
      description: 'Track sales, popular items, and customer preferences.'
    },
    {
      icon: '⚙️',
      title: 'Customizable',
      description: 'Personalize your cafe branding, themes, and feature settings.'
    }
  ];

  return (
    <div className="home-landing fade-in">
      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <h1>BeanBuzz</h1>
          <p>The complete cafe management system that transforms how you run your business</p>
          <div className="hero-actions">
            <button 
              className="btn btn-primary btn-large"
              onClick={() => setShowAuth(true)}
            >
              Get Started Today
            </button>
            <Link 
              to="/beanbuzz-demo/dashboard"
              className="btn btn-secondary btn-large"
              style={{ textDecoration: 'none', display: 'inline-block' }}
            >
              🧪 Try Demo
            </Link>
            <button 
              className="btn btn-ghost btn-large"
              onClick={() => {
                document.getElementById('features')?.scrollIntoView({ 
                  behavior: 'smooth' 
                });
              }}
            >
              Learn More
            </button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-container">
          <h2 className="section-title">Everything you need to run your cafe</h2>
          <p className="section-subtitle">
            From order management to customer engagement, BeanBuzz provides all the tools 
            your cafe needs to thrive in the digital age.
          </p>
          
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className="feature-icon">{feature.icon}</div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="section-container">
          <div className="benefits-content">
            <div className="benefits-text">
              <h2>Streamline operations.<br />Delight customers.</h2>
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-icon">⚡</div>
                  <div>
                    <h4>Faster Service</h4>
                    <p>Reduce wait times with digital ordering and real-time kitchen updates</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">💰</div>
                  <div>
                    <h4>Increase Revenue</h4>
                    <p>Upsell opportunities and data-driven menu optimization</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-icon">😊</div>
                  <div>
                    <h4>Happy Customers</h4>
                    <p>Contactless ordering and personalized experiences</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="benefits-visual">
              <div className="demo-screen">
                <div className="demo-header">BeanBuzz Demo</div>
                <div className="demo-content">
                  <div className="demo-order">
                    <div className="demo-item">☕ Cappuccino - $4.50</div>
                    <div className="demo-item">🥐 Croissant - $3.25</div>
                    <div className="demo-status">✅ Order Ready</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="section-container">
          <div className="cta-content">
            <h2>Ready to transform your cafe?</h2>
            <p>Join hundreds of cafes already using BeanBuzz to improve their operations</p>
            <button 
              className="btn btn-primary btn-large"
              onClick={() => setShowAuth(true)}
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      {/* Authentication Modal */}
      {showAuth && (
        <div className="auth-modal-overlay" onClick={() => setShowAuth(false)}>
          <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
            <button 
              className="modal-close"
              onClick={() => setShowAuth(false)}
            >
              ×
            </button>
            
            <div className="auth-header">
              <h2>Welcome to BeanBuzz</h2>
              <p>Sign in to access your cafe dashboard</p>
            </div>
            
            <div className="auth-tabs">
              <button 
                className={`tab ${isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(true)}
              >
                Sign In
              </button>
              <button 
                className={`tab ${!isLogin ? 'active' : ''}`}
                onClick={() => setIsLogin(false)}
              >
                Sign Up
              </button>
            </div>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="form-group">
                <label htmlFor="email">Email</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="Enter your email"
                />
              </div>

              <div className="form-group">
                <label htmlFor="password">Password</label>
                <input
                  type="password"
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your password"
                  minLength={6}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-block"
                disabled={loading}
              >
                {loading ? 'Please wait...' : (isLogin ? 'Sign In' : 'Sign Up')}
              </button>
            </form>

            <div className="divider">
              <span>or</span>
            </div>

            <button 
              onClick={handleGoogleLogin}
              className="google-btn"
              disabled={loading}
            >
              <svg className="google-icon" viewBox="0 0 24 24" width="20" height="20">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Continue with Google
            </button>
          </div>
        </div>
      )}

      {/* Footer */}
      <footer className="home-footer">
        <div className="footer-content">
          <div className="footer-section">
            <h3>BeanBuzz</h3>
            <p>A complete cafe management system in development</p>
          </div>
          <div className="footer-section">
            <h4>Links</h4>
            <ul>
              <li><Link to="/about">About Us</Link></li>
              <li><Link to="/contact">Contact</Link></li>
              <li><a href="/beanbuzz-demo/dashboard" target="_blank" rel="noopener noreferrer">Demo</a></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Legal</h4>
            <ul>
              <li><Link to="/privacy">Privacy Policy</Link></li>
              <li><Link to="/terms">Terms of Service</Link></li>
            </ul>
          </div>
          <div className="footer-section">
            <h4>Contact</h4>
            <p>Interested in our cafe management system?</p>
            <a href="mailto:vadym@suprun.ca" className="contact-email">vadym@suprun.ca</a>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; 2025 BeanBuzz. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Home; 